import React, { useState, useMemo, useRef, useEffect } from "react"
import { ScrollView, TouchableOpacity, View } from "react-native"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import moment from "moment"
import { connect } from "react-redux"
import {
    apiRequest,
    apiIdle,
    vaporFileUploadIdle,
} from "@data/redux/actions/api"
import { schoolAssessmentsService } from "@data/services/schoolAssessmentsService"
import { chaptersService } from "@data/services/chaptersService"
import { useRequestsBatch } from "@common/hooks/useRequestsBatch"
import MediaPickerButton from "@common/components/media/MediaPickerButton"
import { formIds } from "@data/constants/formConstants"
import AIPreviewSection from "./AIPreviewSection"
import ManualFormSection from "./ManualFormSection"

export const getSchoolAssessmentFormData = (previousAssessment) =>
    previousAssessment
        ? {
              date: previousAssessment.date,
              selectedSubjectId: previousAssessment.school_subject_id,
              selectedChapterIds:
                  previousAssessment.chapters?.map((c) => c.id) || [],
              keyRequirementsText: previousAssessment.key_requirements || "",
              customChapters: [],
          }
        : {
              date: moment().add(7, "days").format("YYYY-MM-DD"),
              selectedSubjectId: null,
              selectedChapterIds: [],
              keyRequirementsText: "",
              customChapters: [],
          }

// Initial state for upload-related values
const INITIAL_UPLOAD_STATE = {
    files: [],
    shouldStart: false,
    processingStage: null,
}

// Initial state for AI detection results
const INITIAL_AI_STATE = {
    previewData: null,
    detectedChapters: [],
}

const SchoolAssessmentTopicForm = ({
    schoolSubjects,
    formData,
    setFormData,

    // All props below are optional
    studentId,
    studentClass,
    studentPedagogicalObjectives,

    isEditing,

    onSubmit,
    loadingSubmit,

    setIsMediaMenuOpen,
    onClose,
    setHeaderStyle,

    externalError,
    // self-managed props (don't pass them)
    apiRequest,
    apiIdle,
    vaporFileUploadIdle,
    isLoadingChapters,
    chapters,
}) => {
    const [error, setError] = useState()
    // Mode state
    const [mode, setMode] = useState(
        isEditing || formData.selectedSubjectId ? "manual" : "ai"
    )
    // AI state (grouped)
    const [aiState, setAiState] = useState(INITIAL_AI_STATE)
    // Upload state (grouped)
    const [uploadState, setUploadState] = useState(INITIAL_UPLOAD_STATE)
    const uploadStartedRef = useRef(false)
    const [images, setImages] = useState([])
    const [pdf, setPdf] = useState(null)
    const hasMedia = images.length > 0 || pdf

    const [datePickerVisible, setDatePickerVisible] = useState(false)

    // Update available chapters when chapters are loaded, merge with AI-detected chapters
    const availableChapters = useMemo(() => {
        if (!formData.selectedSubjectId) return []

        const filtered = chapters?.filter(
            (c) => c.school_subject_id === formData.selectedSubjectId
        )
        const existingIds = new Set(filtered.map((c) => c.id))
        const aiChaptersToAdd = aiState.detectedChapters.filter(
            (c) => !existingIds.has(c.id)
        )
        return [...filtered, ...aiChaptersToAdd]
    }, [chapters, formData.selectedSubjectId, aiState.detectedChapters])

    // Filter subjects by ongoing pedagogical objectives
    // In standard flow: only show subjects from objectives
    // In fast-track flow (no studentPedagogicalObjectives): show all subjects
    const filteredSubjects = useMemo(() => {
        const objectives = studentPedagogicalObjectives || []
        const ongoingObjectives = objectives.filter(
            (obj) => obj?.status !== "closed"
        )
        const subjectIdsFromObjectives = new Set(
            ongoingObjectives.flatMap((obj) => obj?.subject_ids || [])
        )

        if (subjectIdsFromObjectives.size === 0) {
                   return schoolSubjects
                }

        return schoolSubjects.filter((subject) =>
            subjectIdsFromObjectives.has(subject.id)
        )
    }, [schoolSubjects, studentPedagogicalObjectives])

    function getProcessingMessage() {
        switch (uploadState.processingStage) {
            case "uploading":
                return "Envoi..."
            case "analyzing":
                return "Analyse en cours..."
            case "preparing":
                return "Chargement..."
            default:
                return "Analyser"
        }
    }

    function handleSubjectSelect(subjectId) {
        setFormData((prev) => ({
            ...prev,
            selectedSubjectId: subjectId,
            selectedChapterIds: [],
        }))
    }

    function toggleChapter(chapterId) {
        setFormData((prev) => ({
            ...prev,
            selectedChapterIds: prev.selectedChapterIds.includes(chapterId)
                ? prev.selectedChapterIds.filter((id) => id !== chapterId)
                : [...prev.selectedChapterIds, chapterId],
        }))
    }

    function handleAddCustomChapter(chapterName) {
        setFormData((prev) => ({
            ...prev,
            customChapters: [...prev.customChapters, chapterName],
        }))
    }

    function handleRemoveCustomChapter(chapterName) {
        setFormData((prev) => ({
            ...prev,
            customChapters: prev.customChapters.filter(
                (name) => name !== chapterName
            ),
        }))
    }

    function handleProcessAssessment() {
        setError(null)

        // Reset API and vapor upload state to ensure clean state for new upload
        apiIdle(schoolAssessmentsService.processSchoolAssessment)
        vaporFileUploadIdle()

        // Reset the upload started ref to allow new upload
        uploadStartedRef.current = false

        const files = []
        if (images.length > 0) {
            files.push(
                ...images.map((img, index) => ({
                    uri: img?.uri,
                    name: img?.name || `image_${index}.jpg`,
                    type: img?.type || "image/jpeg",
                }))
            )
        }
        if (pdf) {
            files.push({
                uri: pdf,
                name: `document_${Date.now()}.pdf`,
                type: "application/pdf",
            })
        }

        setUploadState({
            files,
            shouldStart: true,
            processingStage: "uploading",
        })
    }

    function handleEditPreview() {
        if (!aiState.previewData) return
        applyAiPreviewData(aiState.previewData)
        setMode("manual")
    }

    // Setup useRequestsBatch for file uploads
    const [uploadProgress, uploadSubmitted, startUpload] = useRequestsBatch(
        [],
        uploadState.shouldStart ? uploadState.files : [],
        formIds.NEW_SCHOOL_ASSESSMENT,
        {
            service: schoolAssessmentsService.processSchoolAssessment,
            params: {},
            data: { student_id: studentId },
            tmpFilesKey: "attachment_files",
            tmpFilesFormatter: (tmpKey) => tmpKey,
            additionalData: [],
            onSuccessBeforeNext: (responseData) => {
                setUploadState((prev) => ({
                    ...prev,
                    processingStage: "preparing",
                    shouldStart: false,
                }))
                uploadStartedRef.current = false

                const data = responseData?.data || responseData
                setAiState({
                    previewData: {
                        subject: data?.subject || null,
                        chapters: data?.chapters || [],
                        exam_date: data?.exam_date || null,
                        key_requirements: data?.key_requirements || [],
                    },
                    detectedChapters: data?.chapters || [],
                })

                applyAiPreviewData(data)
                setError(null)
                setUploadState((prev) => ({ ...prev, processingStage: null }))
                return false
            },
            onError: (err) => {
                resetUploadState()
                setError(err?.message || "Une erreur est survenue")
            },
        },
        [],
        true
    )

    // Start upload when triggered
    useEffect(() => {
        if (
            uploadState.shouldStart &&
            uploadState.files.length > 0 &&
            !uploadStartedRef.current
        ) {
            uploadStartedRef.current = true
            startUpload()
        }
    }, [uploadState.shouldStart, uploadState.files, startUpload])

    // Reset functions
    function resetUploadState() {
        setUploadState(INITIAL_UPLOAD_STATE)
        uploadStartedRef.current = false
    }

    const hasChaptersSelected =
        formData.selectedChapterIds.length > 0 ||
        formData.customChapters.length > 0
    const canSubmit = formData.selectedSubjectId && hasChaptersSelected

    function applyAiPreviewData(data) {
        setFormData((prev) => ({
            ...prev,
            selectedSubjectId: data.subject?.id ?? prev.selectedSubjectId,
            selectedChapterIds:
                data.chapters?.map((c) => c.id) ?? prev.selectedChapterIds,
            keyRequirementsText:
                data.key_requirements?.join("\n") ?? prev.keyRequirementsText,
        }))
        setAiState((prev) => ({ ...prev, detectedChapters: data.chapters }))
    }

    // After 5 seconds of "analyzing", show "preparing"
    useEffect(() => {
        if (uploadState.processingStage === "analyzing") {
            const timer = setTimeout(() => {
                setUploadState((prev) => ({
                    ...prev,
                    processingStage: "preparing",
                }))
            }, 5000)
            return () => clearTimeout(timer)
        }
    }, [uploadState.processingStage])

    // Track upload progress to update processing stage
    useEffect(() => {
        if (
            uploadSubmitted &&
            uploadState.processingStage === "uploading" &&
            uploadProgress > 50
        ) {
            setUploadState((prev) => ({
                ...prev,
                processingStage: "analyzing",
            }))
        }
    }, [uploadProgress, uploadSubmitted, uploadState.processingStage])

    // Fetch chapters when subject changes
    useEffect(() => {
        if (!formData.selectedSubjectId) return
        apiRequest(
            chaptersService.getChaptersBySchoolSubjectId,
            { schoolSubjectId: formData.selectedSubjectId },
            {},
            { class: studentClass }
        )
    }, [formData.selectedSubjectId, studentClass, apiRequest])

    return (
        <>
            {/* Header */}
            <View
                style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 15,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.lightMore,
                    ...setHeaderStyle?.(mode),
                }}
            >
                {onClose ? (
                    <>
                        <TouchableOpacity
                            onPress={
                                mode === "manual" && !isEditing
                                    ? () => setMode("ai")
                                    : onClose
                            }
                            style={{
                                width: 40,
                                height: 40,
                                alignItems: "center",
                                justifyContent: "center",
                                marginLeft: -10,
                            }}
                        >
                            <FontAwesome5
                                name={
                                    mode === "manual" && !isEditing
                                        ? "arrow-left"
                                        : onClose
                                        ? "times"
                                        : null
                                }
                                size={24}
                                color={theme.colors.grey}
                            />
                        </TouchableOpacity>
                        <TextM fontSize={18} fontWeight="Bold">
                            {isEditing
                                ? "Modifier le contrôle"
                                : "Ajouter un contrôle"}
                        </TextM>
                        <View style={{ width: 20 }} />
                    </>
                ) : mode === "manual" ? (
                    <FilledButtonM
                        onPress={() => setMode("ai")}
                        disabled={uploadSubmitted}
                        label="Remplir automatiquement"
                        mode="text"
                        style={{ width: "100%" }}
                        contentStyle={{ height: 30 }}
                    />
                ) : null}
            </View>
            <ScrollView
                contentContainerStyle={{ padding: 20 }}
                keyboardShouldPersistTaps="handled"
            >
                {mode === "ai" && (
                    <View>
                        {/* Intro text - only show when no images and no AI preview */}
                        {!hasMedia && !aiState.previewData && (
                            <View style={{ marginBottom: 20 }}>
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 8,
                                    }}
                                >
                                    <FontAwesome5
                                        name="magic"
                                        size={18}
                                        color={theme.colors.primary}
                                        style={{ marginRight: 8 }}
                                    />
                                    <TextM
                                        fontSize={16}
                                        fontWeight="Bold"
                                        color={theme.colors.primary}
                                    >
                                        Analyse automatique
                                    </TextM>
                                </View>
                                <TextM
                                    fontSize={14}
                                    color={theme.colors.grey}
                                    style={{ textAlign: "center" }}
                                    wrap
                                >
                                    Prends en photo ton cahier de texte, ton
                                    agenda ou une capture de Pronote et on
                                    remplit tout pour toi !
                                </TextM>
                            </View>
                        )}

                        {/* Big Media Picker Button */}
                        <MediaPickerButton
                            images={images}
                            setImages={setImages}
                            pdf={pdf}
                            setPdf={setPdf}
                            buttonLabel="Photo ou document"
                            formId={formIds.NEW_SCHOOL_ASSESSMENT}
                            enableExternalLink={false}
                            onMenuStateChange={setIsMediaMenuOpen}
                            multiple={true}
                            attachments={[
                                ...(pdf ? [{ uri: pdf }] : []),
                                ...(images || []),
                            ]}
                            buttonStyle={{
                                width: "100%",
                                minHeight: 180,
                                alignItems: "center",
                                backgroundColor: theme.colors.primaryLighter,
                                borderStyle: "dashed",
                                borderWidth: 2,
                                borderColor: theme.colors.primary,
                            }}
                            addMoreButtonStyle={{ width: "100%" }}
                            showAddMoreButton={
                                !aiState.previewData && !uploadSubmitted
                            }
                        />

                        {/* Analyze button */}
                        {hasMedia && !aiState.previewData && (
                            <FilledButtonM
                                onPress={handleProcessAssessment}
                                label={getProcessingMessage()}
                                icon={uploadSubmitted ? null : "magic"}
                                loading={uploadSubmitted}
                                wrap
                                style={{ width: "100%", marginTop: 15 }}
                            />
                        )}

                        {/* AI Preview Section */}
                        <AIPreviewSection
                            aiPreviewData={aiState.previewData}
                            onEdit={handleEditPreview}
                        />

                        {/* Divider with "ou" */}
                        {!aiState.previewData && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginVertical: 20,
                                }}
                            >
                                <View
                                    style={{
                                        flex: 1,
                                        height: 1,
                                        backgroundColor: theme.colors.lightMore,
                                    }}
                                />
                                <TextM
                                    fontSize={13}
                                    color={theme.colors.light}
                                    style={{ marginHorizontal: 15 }}
                                >
                                    ou
                                </TextM>
                                <View
                                    style={{
                                        flex: 1,
                                        height: 1,
                                        backgroundColor: theme.colors.lightMore,
                                    }}
                                />
                            </View>
                        )}

                        {/* Manual entry button */}
                        {!aiState.previewData && (
                            <FilledButtonM
                                onPress={() => setMode("manual")}
                                disabled={uploadSubmitted}
                                label="Remplir à la main"
                                mode="text"
                                style={{ width: "100%" }}
                            />
                        )}
                    </View>
                )}

                {/* Manual Mode: Form fields */}
                {mode === "manual" && (
                    <ManualFormSection
                        isEditing={isEditing}
                        filteredSubjects={filteredSubjects}
                        selectedSubjectId={formData.selectedSubjectId}
                        onSubjectSelect={handleSubjectSelect}
                        watchedDate={formData.date}
                        onDateChange={(date) =>
                            setFormData((prev) => ({ ...prev, date: date }))
                        }
                        datePickerVisible={datePickerVisible}
                        setDatePickerVisible={setDatePickerVisible}
                        availableChapters={availableChapters}
                        selectedChapterIds={formData.selectedChapterIds}
                        onToggleChapter={toggleChapter}
                        customChapters={formData.customChapters}
                        onAddCustomChapter={handleAddCustomChapter}
                        onRemoveCustomChapter={handleRemoveCustomChapter}
                        keyRequirementsText={formData.keyRequirementsText}
                        onKeyRequirementsChange={(text) =>
                            setFormData((prev) => ({
                                ...prev,
                                keyRequirementsText: text,
                            }))
                        }
                        chaptersLoading={isLoadingChapters}
                        loading={loadingSubmit}
                    />
                )}
            </ScrollView>
            {/* Error message and Submit button */}
            <View
                style={{
                    padding: 20,
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.lightMore,
                }}
            >
                {(error || externalError) && (
                    <View
                        style={{
                            backgroundColor: theme.colors.errorLight,
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 12,
                            flexDirection: "row",
                            alignItems: "center",
                        }}
                    >
                        <FontAwesome5
                            name="exclamation-circle"
                            size={16}
                            color={theme.colors.error}
                            style={{ marginRight: 10 }}
                        />
                        <TextM
                            fontSize={13}
                            color={theme.colors.error}
                            style={{ flex: 1 }}
                            wrap
                        >
                            {error || externalError}
                        </TextM>
                    </View>
                )}

                {/* Helper text when button is disabled in manual mode */}
                {mode === "manual" && !error && (
                    <>
                        {!formData.selectedSubjectId && (
                            <TextM
                                fontSize={12}
                                color={theme.colors.light}
                                style={{
                                    textAlign: "center",
                                    marginBottom: 10,
                                }}
                            >
                                Choisis une matière pour continuer
                            </TextM>
                        )}
                        {formData.selectedSubjectId && !hasChaptersSelected && (
                            <TextM
                                fontSize={12}
                                color={theme.colors.light}
                                style={{
                                    textAlign: "center",
                                    marginBottom: 10,
                                }}
                            >
                                Choisis au moins un chapitre pour continuer
                            </TextM>
                        )}
                    </>
                )}

                <FilledButtonM
                    onPress={onSubmit}
                    label={isEditing ? "Enregistrer" : "C'est parti !"}
                    loading={loadingSubmit}
                    disabled={!canSubmit}
                    icon={isEditing ? null : "check"}
                />
            </View>
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        chapters: state.data.chapters?.chaptersList || [],
        isLoadingChapters:
            state.api.getChaptersBySchoolSubjectId?.request || false,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    vaporFileUploadIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SchoolAssessmentTopicForm)
